import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Filter, Search } from "lucide-react";
import type {
  CompanySearchResult,
  WorkspaceList,
  SavedCompanySearch,
} from "@sales-ops-amplify/shared";
import { workspaceAccountDetailId } from "@/lib/workspace-account";
import { cn } from "@/cn";
import {
  createSavedCompanySearch,
  exportCompaniesCsv,
  listSavedCompanySearches,
  saveCompanies,
  searchCompanies,
} from "@/api/companies";
import {
  bulkDeleteAccounts,
  deleteAccount,
  exportAccountsCsv,
} from "@/api/accounts";
import { downloadBlob } from "@/api/download";
import {
  createWorkspaceList,
  listWorkspaceLists,
} from "@/api/lists";
import { addDiscoveredOffers } from "@/api/discovery";
import { listIcps } from "@/api/icp";
import {
  EMPTY_COMPANY_FILTERS,
  CompanyFilters,
  apiFiltersToState,
  filtersToApi,
  seedCompanyFiltersFromIcp,
  type CompanyFilterState,
} from "@/components/companies/company-filters";
import {
  buildCompanyFilterChips,
  CompanyFilterChips,
} from "@/components/companies/company-filter-chips";
import {
  decodeFiltersParam,
  encodeFiltersParam,
} from "@/components/companies/company-filter-config";
import { CompanyResearchAiModal } from "@/components/companies/company-research-ai-modal";
import {
  CompanyResultsTable,
  companyRowId,
} from "@/components/companies/company-results-table";
import { MobileSheet } from "@/components/mobile-sheet";
import { WorkflowEnrollPicker } from "@/components/workflows/enroll-picker";
import { leadToExploreResult, parseLeadEnvelopes } from "@/lib/lead-envelope";

type Mode = "total" | "net-new";

export type CompaniesDirectoryScope = "directory" | "account";

export function CompaniesDirectoryPage({
  scope,
}: {
  scope: CompaniesDirectoryScope;
}) {
  const isAccount = scope === "account";
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<CompanyFilterState>(() => {
    const encoded = searchParams.get("filters");
    if (encoded) {
      const decoded = decodeFiltersParam(encoded);
      if (decoded && typeof decoded === "object") {
        return { ...EMPTY_COMPANY_FILTERS, ...(decoded as CompanyFilterState) };
      }
    }
    return EMPTY_COMPANY_FILTERS;
  });
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [topSearch, setTopSearch] = useState(
    () => searchParams.get("q") ?? "",
  );
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [mode, setMode] = useState<Mode>("total");
  // Mine scope has no net-new concept — all rows are owned copies.
  const [companies, setCompanies] = useState<CompanySearchResult[]>([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveSearchOpen, setSaveSearchOpen] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState("");
  const [searching, setSearching] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const [researchOpen, setResearchOpen] = useState(false);
  const [listPicker, setListPicker] = useState(false);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [newListName, setNewListName] = useState("");
  const autoRan = useRef(false);
  const lastUrlQ = useRef<string | null>(null);

  const { data: savedData } = useQuery({
    queryKey: ["saved-company-searches"],
    queryFn: listSavedCompanySearches,
    staleTime: 30_000,
  });
  const savedSearches = savedData?.searches ?? [];

  const { data: listsData } = useQuery({
    queryKey: ["workspace-lists", "companies"],
    queryFn: () => listWorkspaceLists({ modality: "companies" }),
    staleTime: 30_000,
  });
  const lists = listsData?.lists ?? [];

  const { data: icpData } = useQuery({
    queryKey: ["icp"],
    queryFn: listIcps,
    staleTime: 60_000,
  });
  const icps = icpData?.icps ?? [];

  const saveMutation = useMutation({
    mutationFn: (args: { companies: CompanySearchResult[]; listId?: string }) =>
      saveCompanies(args),
    onSuccess: (res, vars) => {
      setMessage(`Added ${res.saved} to accounts, skipped ${res.skipped}`);
      setError(null);
      const accountIdByCompanyId = new Map(
        res.accounts
          .filter((account) => account.companyId)
          .map((account) => [account.companyId!, account.accountId]),
      );
      const selectedCompanyIds = new Set(
        vars.companies.map((company) => company.companyId),
      );
      setCompanies((prev) =>
        prev.map((c) =>
          selectedCompanyIds.has(c.companyId)
            ? {
                ...c,
                alreadySaved: true,
                accountId:
                  accountIdByCompanyId.get(c.companyId) ?? c.accountId,
              }
            : c,
        ),
      );
      setSelectedIds(new Set());
      qc.invalidateQueries({ queryKey: ["workspace-lists"] });
      runSearch(page);
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Save failed");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (slugs: string[]) => {
      for (const slug of slugs) {
        await deleteAccount(slug);
      }
      return slugs.length;
    },
    onSuccess: (n) => {
      setMessage(
        `Moved ${n} Account${n === 1 ? "" : "s"} to the recycling bin`,
      );
      setError(null);
      setSelectedIds(new Set());
      runSearch(page);
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Delete failed");
    },
  });

  const deleteAllMatchingMutation = useMutation({
    mutationFn: () =>
      bulkDeleteAccounts({
        allMatching: true,
        filters: {
          ...filtersToApi(mergeTopSearch(filters, topSearch), 1, perPage),
          scope: "account",
        },
      }),
    onSuccess: (res) => {
      setMessage(
        `Moved ${res.deleted} matching Account${res.deleted === 1 ? "" : "s"} to the recycling bin`,
      );
      setError(null);
      setSelectedIds(new Set());
      runSearch(1);
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Delete all matching failed");
    },
  });

  const createListMutation = useMutation({
    mutationFn: (name: string) =>
      createWorkspaceList({ name, modality: "companies" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workspace-lists"] });
      setNewListName("");
    },
  });

  const saveSearchMutation = useMutation({
    mutationFn: (name: string) =>
      createSavedCompanySearch({
        name,
        filters: filtersToApi(
          mergeTopSearch(filters, topSearch),
          page,
          perPage,
        ),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["saved-company-searches"] });
      setSaveSearchOpen(false);
      setSaveSearchName("");
      setMessage("Search saved");
    },
  });

  const savedOnPage = useMemo(
    () => companies.filter((c) => c.alreadySaved).length,
    [companies],
  );
  const netNewOnPage = companies.length - savedOnPage;

  const visibleCompanies = useMemo(() => {
    if (!isAccount && mode === "net-new") {
      return companies.filter((c) => !c.alreadySaved);
    }
    return companies;
  }, [companies, mode, isAccount]);

  const selectedCompanies = useMemo(
    () => companies.filter((c) => selectedIds.has(companyRowId(c))),
    [companies, selectedIds],
  );

  function syncUrl(nextFilters: CompanyFilterState, q: string) {
    const params = new URLSearchParams();
    const trimmed = q.trim();
    if (trimmed) params.set("q", trimmed);
    const encoded = encodeFiltersParam(nextFilters);
    if (encoded !== encodeFiltersParam(EMPTY_COMPANY_FILTERS)) {
      params.set("filters", encoded);
    }
    lastUrlQ.current = trimmed;
    setSearchParams(params, { replace: true });
  }

  function runSearch(
    nextPage = page,
    nextFilters = filters,
    nextTop = topSearch,
    nextPerPage = perPage,
  ) {
    setPage(nextPage);
    setSearching(true);
    syncUrl(nextFilters, nextTop);
    const apiFilters = {
      ...filtersToApi(
        mergeTopSearch(nextFilters, nextTop),
        nextPage,
        nextPerPage,
      ),
      scope,
    };
    searchCompanies(apiFilters)
      .then((res) => {
        setCompanies(res.companies);
        setTotalEntries(res.totalEntries);
        setSelectedIds(new Set());
        setError(null);
        setMessage(`Found ${res.totalEntries.toLocaleString()} companies`);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Search failed");
      })
      .finally(() => setSearching(false));
  }

  /** Reset sidebar filters + page search bar and re-query. */
  function clearFiltersAndSearch() {
    setFilters(EMPTY_COMPANY_FILTERS);
    setTopSearch("");
    runSearch(1, EMPTY_COMPANY_FILTERS, "");
  }

  /** Chip remove / edit that should immediately re-search. */
  function applyFilterChange(next: CompanyFilterState) {
    setFilters(next);
    runSearch(1, next, topSearch);
  }

  function changePerPage(n: number) {
    setPerPage(n);
    runSearch(1, filters, topSearch, n);
  }

  useEffect(() => {
    if (autoRan.current) return;
    autoRan.current = true;
    const q = searchParams.get("q") ?? "";
    lastUrlQ.current = q.trim();
    runSearch(1, filters, q || topSearch);
    // Load directory from DynamoDB on first visit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Top-bar (or external) ?q= changes while staying on this page.
  useEffect(() => {
    if (!autoRan.current) return;
    const q = (searchParams.get("q") ?? "").trim();
    if (lastUrlQ.current === q) return;
    lastUrlQ.current = q;
    setTopSearch(q);
    runSearch(1, filters, q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  function applySavedSearch(search: SavedCompanySearch) {
    const next = apiFiltersToState(search.filters);
    setFilters(next);
    setTopSearch("");
    setPage(1);
    setMessage(`Loaded “${search.name}”`);
    runSearch(1, next, "");
  }

  function seedFromIcp() {
    const active =
      icps.find((i) => i.id === icpData?.activeIcpId) ?? icps[0];
    if (!active) {
      setMessage("No ICP found. Create one under Settings → Personas.");
      return;
    }
    const next = seedCompanyFiltersFromIcp(active, filters);
    setFilters(next);
    setMessage(`Seeded filters from ICP “${active.name}”`);
    runSearch(1, next, topSearch);
  }

  async function onExport() {
    const rows = selectedCompanies.length ? selectedCompanies : visibleCompanies;
    if (rows.length === 0) return;
    try {
      if (isAccount) {
        const slugs = rows
          .map((c) => c.accountId)
          .filter((s): s is string => Boolean(s));
        const blob = await exportAccountsCsv(slugs.length ? slugs : undefined);
        downloadBlob(blob, "accounts-export.csv");
      } else {
        const blob = await exportCompaniesCsv(rows);
        downloadBlob(blob, "companies-export.csv");
      }
      setMessage(`Exported ${rows.length} rows`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Export failed");
    }
  }

  const [workflowPickerOpen, setWorkflowPickerOpen] = useState(false);

  function createWorkflow() {
    if (selectedCompanies.length === 0) {
      navigate("/workflows");
      return;
    }
    setWorkflowPickerOpen(true);
  }

  async function saveResearchAsDeals(text: string) {
    const leads = parseLeadEnvelopes(text);
    if (!leads.length) {
      setMessage("No structured leads found to save as deals.");
      return;
    }
    try {
      const offers = leads.map((l, i) => {
        const r = leadToExploreResult(l, i);
        return {
          url: r.url,
          company: r.company,
          role: r.title,
          dealName: r.title,
          location: r.location,
          compensation: r.compensation,
          source: r.source,
        };
      });
      const res = await addDiscoveredOffers(offers, "companies-research-ai");
      setMessage(`Saved ${res.added.length} deals (skipped ${res.skipped})`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save deals failed");
    }
  }

  const icpContext = useMemo(() => {
    const active =
      icps.find((i) => i.id === filters.personaIcpId) ??
      icps.find((i) => i.id === icpData?.activeIcpId) ??
      icps[0];
    if (!active) return undefined;
    return [
      `Name: ${active.name}`,
      active.industries?.length
        ? `Industries: ${active.industries.join(", ")}`
        : "",
      active.geographies?.length
        ? `Geographies: ${active.geographies.join(", ")}`
        : "",
      active.icpKeywords?.length
        ? `Keywords: ${active.icpKeywords.join(", ")}`
        : "",
    ]
      .filter(Boolean)
      .join("\n");
  }, [icps, filters.personaIcpId, icpData?.activeIcpId]);

  const totalPages = Math.max(1, Math.ceil(totalEntries / perPage));
  const activeFilterCount = buildCompanyFilterChips(filters).length;

  return (
    <div className="flex h-full min-h-0 gap-3 overflow-hidden">
      <div className="hidden h-full md:block">
        <CompanyFilters
          value={filters}
          onChange={setFilters}
          onSearch={() => runSearch(1)}
          onClear={clearFiltersAndSearch}
          searching={searching}
          collapsed={filtersCollapsed}
          onToggleCollapsed={() => setFiltersCollapsed((c) => !c)}
          icps={icps}
          activeIcpId={icpData?.activeIcpId}
          workspaceScope={isAccount}
        />
      </div>

      <MobileSheet
        open={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        title="Filters"
        variant="full"
      >
        <CompanyFilters
          embedded
          value={filters}
          onChange={setFilters}
          onSearch={() => {
            setMobileFiltersOpen(false);
            runSearch(1);
          }}
          onClear={() => {
            clearFiltersAndSearch();
            setMobileFiltersOpen(false);
          }}
          searching={searching}
          icps={icps}
          activeIcpId={icpData?.activeIcpId}
          workspaceScope={isAccount}
        />
      </MobileSheet>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-border bg-card">
        <div className="sticky top-0 z-20 shrink-0 border-b border-border bg-card">
          <div className="flex flex-wrap items-center gap-2 px-4 py-3 sm:gap-3 sm:px-5 sm:py-4">
            <h1 className="text-lg font-bold text-foreground">
              {isAccount ? "Accounts" : "Find companies"}
            </h1>
            <form
              className="relative min-w-0 flex-1 basis-full sm:basis-auto sm:min-w-[160px]"
              onSubmit={(e) => {
                e.preventDefault();
                runSearch(1);
              }}
            >
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <input
                value={topSearch}
                onChange={(e) => setTopSearch(e.target.value)}
                placeholder="Search companies by name or keyword…"
                className="w-full rounded-md bg-surface-hover py-2 pl-8 pr-3 text-sm outline-none focus:ring-1 focus:ring-border"
              />
            </form>
            <button
              type="button"
              onClick={() => setMobileFiltersOpen(true)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md bg-surface-hover px-2.5 py-1.5 text-xs font-medium text-foreground md:hidden",
                activeFilterCount > 0 && "ring-1 ring-brand/40",
              )}
            >
              <Filter className="size-3.5" />
              Filters
              {activeFilterCount > 0 && (
                <span className="rounded-full bg-brand px-1.5 py-0.5 text-[10px] text-brand-foreground">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {isAccount ? (
              <span className="rounded-md bg-surface-hover px-2.5 py-1 text-xs font-medium text-muted-foreground">
                {totalEntries.toLocaleString()} accounts
              </span>
            ) : (
              <div className="flex items-center gap-1 rounded-md bg-surface-hover p-0.5">
                <button
                  type="button"
                  onClick={() => setMode("total")}
                  className={cn(
                    "rounded px-2.5 py-1 text-xs font-medium",
                    mode === "total"
                      ? "bg-brand text-brand-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  Total ({totalEntries.toLocaleString()})
                </button>
                <button
                  type="button"
                  onClick={() => setMode("net-new")}
                  className={cn(
                    "rounded px-2.5 py-1 text-xs font-medium",
                    mode === "net-new"
                      ? "bg-brand text-brand-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  Net new ({netNewOnPage})
                </button>
              </div>
            )}
            <div className="relative">
              <button
                type="button"
                onClick={() => setActionsMenuOpen((o) => !o)}
                className="rounded-md bg-surface-hover px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-surface-hover/80"
              >
                Actions
              </button>
              {actionsMenuOpen && (
                <div className="absolute right-0 z-30 mt-1 w-52 rounded-md bg-popover py-1 shadow-xl ring-1 ring-black/5 dark:ring-white/10">
                  <Link
                    to={isAccount ? "/companies" : "/user/accounts"}
                    className="block px-3 py-1.5 text-xs hover:bg-surface-hover"
                    onClick={() => setActionsMenuOpen(false)}
                  >
                    {isAccount ? "Find in directory" : "Accounts"}
                  </Link>
                  <button
                    type="button"
                    className="block w-full px-3 py-1.5 text-left text-xs hover:bg-surface-hover"
                    onClick={() => {
                      setActionsMenuOpen(false);
                      setSaveSearchOpen(true);
                    }}
                  >
                    Save search
                  </button>
                  <button
                    type="button"
                    className="block w-full px-3 py-1.5 text-left text-xs hover:bg-surface-hover disabled:opacity-40"
                    disabled={visibleCompanies.length === 0}
                    onClick={() => {
                      setActionsMenuOpen(false);
                      onExport();
                    }}
                  >
                    Export
                  </button>
                  <button
                    type="button"
                    className="block w-full px-3 py-1.5 text-left text-xs hover:bg-surface-hover disabled:opacity-40"
                    disabled={selectedIds.size === 0}
                    onClick={() => {
                      setActionsMenuOpen(false);
                      setListPicker(true);
                    }}
                  >
                    {isAccount ? "Add selection to list" : "Save selection to list"}
                  </button>
                  <Link
                    to="/lists"
                    className="block px-3 py-1.5 text-xs hover:bg-surface-hover"
                    onClick={() => setActionsMenuOpen(false)}
                  >
                    Manage lists
                  </Link>
                  <button
                    type="button"
                    className="block w-full px-3 py-1.5 text-left text-xs hover:bg-surface-hover"
                    onClick={() => {
                      setActionsMenuOpen(false);
                      setResearchOpen(true);
                    }}
                  >
                    Research with AI
                  </button>
                  <button
                    type="button"
                    className="block w-full px-3 py-1.5 text-left text-xs hover:bg-surface-hover disabled:opacity-40"
                    disabled={selectedIds.size === 0}
                    onClick={() => {
                      setActionsMenuOpen(false);
                      createWorkflow();
                    }}
                  >
                    Workflows
                  </button>
                  {isAccount && (
                    <button
                      type="button"
                      className="block w-full px-3 py-1.5 text-left text-xs text-destructive hover:bg-surface-hover disabled:opacity-40"
                      disabled={
                        totalEntries === 0 || deleteAllMatchingMutation.isPending
                      }
                      onClick={() => {
                        setActionsMenuOpen(false);
                        if (
                          !window.confirm(
                            `Move all ${totalEntries} matching Account${totalEntries === 1 ? "" : "s"} to the recycling bin? You can restore them from Settings.`,
                          )
                        ) {
                          return;
                        }
                        deleteAllMatchingMutation.mutate();
                      }}
                    >
                      Delete all matching
                    </button>
                  )}
                  {isAccount && (
                    <Link
                      to="/settings/recycling-bin/accounts"
                      className="block px-3 py-1.5 text-xs hover:bg-surface-hover"
                      onClick={() => setActionsMenuOpen(false)}
                    >
                      Recycling bin
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2.5 px-5 pb-4">
            <CompanyFilterChips
              value={filters}
              onChange={applyFilterChange}
              onClear={clearFiltersAndSearch}
              personaLabel={
                icps.find((i) => i.id === filters.personaIcpId)?.name
              }
            />
            {savedSearches.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                <span className="text-muted-foreground">Saved searches:</span>
                {savedSearches.map((s) => (
                  <button
                    key={s.searchId}
                    type="button"
                    onClick={() => applySavedSearch(s)}
                    className="rounded bg-surface-hover px-2 py-1 hover:text-foreground"
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {showBanner && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border bg-muted/15 px-5 py-2.5 text-xs">
              <p className="text-muted-foreground">
                {isAccount
                  ? "Your mutable company copies. Edits, notes, and enrichment stay on your records — the directory is unchanged."
                  : "Search the company directory. Save a company to create your mutable copy under Find & enrich."}
              </p>
              <div className="flex items-center gap-2">
                {!isAccount && (
                  <button
                    type="button"
                    onClick={seedFromIcp}
                    className="rounded-md bg-brand px-2.5 py-1 font-medium text-brand-foreground"
                  >
                    Seed from ICP
                  </button>
                )}
                {!isAccount && (
                  <Link
                    to="/settings/integrations"
                    className="text-brand hover:underline"
                  >
                    Integrations
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => setShowBanner(false)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}
        </div>

        {(message || error) && (
          <p
            className={cn(
              "shrink-0 px-5 py-2.5 text-xs",
              error
                ? "bg-destructive/10 text-destructive"
                : "text-muted-foreground",
            )}
          >
            {error ?? message}
          </p>
        )}

        <CompanyResultsTable
          companies={visibleCompanies}
          selectedIds={selectedIds}
          onToggle={(id) =>
            setSelectedIds((prev) => {
              const next = new Set(prev);
              if (next.has(id)) next.delete(id);
              else next.add(id);
              return next;
            })
          }
          onToggleAll={(all) =>
            setSelectedIds(
              all
                ? new Set(visibleCompanies.map((c) => companyRowId(c)))
                : new Set(),
            )
          }
          onOpen={(c) => {
            const id = isAccount
              ? workspaceAccountDetailId(c)
              : (c.companyId ?? c.accountId ?? c.apolloOrgId);
            if (!id) return;
            navigate(isAccount ? `/user/accounts/${id}` : `/companies/${id}`);
          }}
        />

        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-border bg-card px-5 py-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <button
              type="button"
              disabled={page <= 1 || searching}
              onClick={() => runSearch(page - 1)}
              className="rounded-md border border-border px-2 py-1 disabled:opacity-40"
            >
              Prev
            </button>
            <span>
              Page {page} of {totalPages}
              {isAccount
                ? ""
                : mode === "net-new"
                  ? ` · ${netNewOnPage} net new on this page`
                  : ` · ${savedOnPage} already in accounts on this page`}
            </span>
            <button
              type="button"
              disabled={page >= totalPages || searching}
              onClick={() => runSearch(page + 1)}
              className="rounded-md border border-border px-2 py-1 disabled:opacity-40"
            >
              Next
            </button>
          </div>
          <select
            value={perPage}
            onChange={(e) => changePerPage(Number(e.target.value))}
            className="rounded-md border border-border bg-bg px-2 py-1 text-xs"
          >
            {[10, 25, 50].map((n) => (
              <option key={n} value={n}>
                {n} / page
              </option>
            ))}
          </select>
        </div>

        {selectedIds.size > 0 && (
          <div className="sticky bottom-0 z-20 flex flex-wrap items-center gap-2 border-t border-border bg-card px-5 py-3.5 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
            <span className="text-sm font-semibold">
              {selectedIds.size} selected
            </span>
            {!isAccount && (
              <button
                type="button"
                disabled={saveMutation.isPending}
                onClick={() =>
                  saveMutation.mutate({ companies: selectedCompanies })
                }
                className="rounded-md bg-brand px-2.5 py-1.5 text-xs font-medium text-brand-foreground disabled:opacity-50"
              >
                Save
              </button>
            )}
            {isAccount && (
              <button
                type="button"
                disabled={deleteMutation.isPending}
                onClick={() => {
                  const slugs = selectedCompanies
                    .map((c) => workspaceAccountDetailId(c))
                    .filter((id): id is string => !!id);
                  if (!slugs.length) return;
                  if (
                    !window.confirm(
                      `Move ${slugs.length} Account${slugs.length === 1 ? "" : "s"} to the recycling bin? You can restore them from Settings.`,
                    )
                  ) {
                    return;
                  }
                  deleteMutation.mutate(slugs);
                }}
                className="rounded-md border border-destructive/40 px-2.5 py-1.5 text-xs text-destructive disabled:opacity-50"
              >
                Delete
              </button>
            )}
            {isAccount && (
              <button
                type="button"
                onClick={() => setListPicker(true)}
                className="rounded-md border border-border px-2.5 py-1.5 text-xs"
              >
                Add to list
              </button>
            )}
            {isAccount && (
              <button
                type="button"
                onClick={createWorkflow}
                className="rounded-md border border-border px-2.5 py-1.5 text-xs"
              >
                Workflows
              </button>
            )}
            {!isAccount && (
              <button
                type="button"
                onClick={() => setListPicker(true)}
                className="rounded-md border border-border px-2.5 py-1.5 text-xs"
              >
                Save to list
              </button>
            )}
            <button
              type="button"
              onClick={onExport}
              className="rounded-md border border-border px-2.5 py-1.5 text-xs"
            >
              Export CSV
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="ml-auto text-xs text-muted-foreground hover:text-foreground"
            >
              Clear selection
            </button>
          </div>
        )}
      </div>

      {saveSearchOpen && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4"
          onClick={() => setSaveSearchOpen(false)}
        >
          <div
            className="w-full max-w-sm rounded-lg border border-border bg-card p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold">Save search</h3>
            <input
              value={saveSearchName}
              onChange={(e) => setSaveSearchName(e.target.value)}
              placeholder="Search name"
              className="mt-3 w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
            />
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setSaveSearchOpen(false)}
                className="text-xs text-muted-foreground"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!saveSearchName.trim() || saveSearchMutation.isPending}
                onClick={() => saveSearchMutation.mutate(saveSearchName.trim())}
                className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {workflowPickerOpen && (
        <WorkflowEnrollPicker
          accountIds={selectedCompanies.map(
            (c) => c.accountId || c.apolloOrgId || c.companyId,
          )}
          onClose={() => setWorkflowPickerOpen(false)}
        />
      )}

      <CompanyResearchAiModal
        open={researchOpen}
        onClose={() => setResearchOpen(false)}
        icpContext={icpContext}
        onApplyKeywords={(tags) => {
          setFilters((f) => ({
            ...f,
            qOrganizationKeywordTags: [
              ...new Set([...f.qOrganizationKeywordTags, ...tags]),
            ],
          }));
          setMessage("Keywords applied from AI research");
        }}
        onSaveAsDeals={(text) => {
          void saveResearchAsDeals(text);
        }}
      />

      {listPicker && (
        <CompanyListPickerModal
          lists={lists}
          newListName={newListName}
          onNewListName={setNewListName}
          creating={createListMutation.isPending}
          onCreate={() => {
            if (newListName.trim())
              createListMutation.mutate(newListName.trim());
          }}
          onPick={(listId) => {
            saveMutation.mutate({
              companies: selectedCompanies,
              listId,
            });
            setListPicker(false);
          }}
          onClose={() => setListPicker(false)}
        />
      )}
    </div>
  );
}

function CompanyListPickerModal({
  lists,
  newListName,
  onNewListName,
  onCreate,
  creating,
  onPick,
  onClose,
}: {
  lists: WorkspaceList[];
  newListName: string;
  onNewListName: (v: string) => void;
  onCreate: () => void;
  creating?: boolean;
  onPick: (listId: string) => void;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-lg border border-border bg-card p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-sm font-semibold">Save to company list</h3>
        <ul className="mt-3 max-h-48 space-y-1 overflow-y-auto">
          {lists.length === 0 && (
            <li className="text-xs text-muted-foreground">No lists yet.</li>
          )}
          {lists.map((l) => (
            <li key={l.listId}>
              <button
                type="button"
                onClick={() => onPick(l.listId)}
                className="w-full rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted/20"
              >
                {l.name}{" "}
                <span className="text-xs text-muted-foreground">
                  ({l.memberCount})
                </span>
              </button>
            </li>
          ))}
        </ul>
        <div className="mt-3 flex gap-2">
          <input
            value={newListName}
            onChange={(e) => onNewListName(e.target.value)}
            placeholder="New list name"
            className="flex-1 rounded-md border border-border bg-bg px-2 py-1.5 text-sm"
          />
          <button
            type="button"
            disabled={!newListName.trim() || creating}
            onClick={onCreate}
            className="rounded-md border border-border px-2 py-1.5 text-xs disabled:opacity-50"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

function mergeTopSearch(
  filters: CompanyFilterState,
  topSearch: string,
): CompanyFilterState {
  const q = topSearch.trim();
  if (!q) return filters;
  if (filters.qOrganizationName.trim()) return filters;
  return { ...filters, qOrganizationName: q };
}
